/**
 * useAdminFocusMode
 * ------------------
 * Pures Client-Side Toggle für Admins: Wenn aktiv, wird das UI gerendert,
 * als wäre der eingeloggte Admin ein „normaler" Mitarbeiter (ohne Spezial-Permissions).
 *
 * Verwendung:
 *   const { focusMode, setFocusMode } = useAdminFocusMode();
 *   const effectiveRole = (focusMode && user.role === 'admin') ? 'mitarbeiter' : user.role;
 *
 * Persistenz: localStorage (`admin_focus_mode` = 'true'/null).
 * Cross-Component-Sync: via Custom-Event `admin-focus-mode-change`.
 */
import { useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'admin_focus_mode';
const EVENT_NAME = 'admin-focus-mode-change';

const readFlag = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

export function useAdminFocusMode() {
  const [focusMode, setFocusModeState] = useState(readFlag);

  // Sync zwischen Tabs & innerhalb der App
  useEffect(() => {
    const handler = () => setFocusModeState(readFlag());
    window.addEventListener(EVENT_NAME, handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const setFocusMode = useCallback((value) => {
    try {
      if (value) {
        localStorage.setItem(STORAGE_KEY, 'true');
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch { /* noop */ }
    setFocusModeState(!!value);
    // Inform andere Komponenten in derselben Seite
    window.dispatchEvent(new Event(EVENT_NAME));
  }, []);

  return { focusMode, setFocusMode };
}

/**
 * Bequemer Helper: Wenn Focus-Modus aktiv UND User ist Admin → effektive Rolle = 'mitarbeiter'.
 * Sonst die echte Rolle.
 */
export function getEffectiveRole(user, focusMode) {
  if (focusMode && user?.role === 'admin') return 'mitarbeiter';
  return user?.role;
}

/**
 * Permissions im Focus-Modus auf Standard-Mitarbeiter zurücksetzen.
 * Ohne Focus-Modus: Original-Permissions zurückgeben.
 */
export function getEffectivePermissions(user, focusMode) {
  if (focusMode && user?.role === 'admin') {
    // Standard Mitarbeiter ohne Spezial-Rechte
    return {};
  }
  return user?.permissions || {};
}
